/**
 * lib/hodAnalysisXlsx.ts
 * ------------------------------------------------------------------
 * Render the HOD "Teacher-to-Teacher" analysis (lib/hodAnalysis) to a
 * real .xlsx workbook — one sheet per subject, laid out exactly like the
 * department template: a title, then a block per form with a bold header,
 * one row per teacher (merged classes), grade columns A*..U, a Total and
 * Credit Pass % (A*-C) / Pass % (A*-E) as live formulas, and a per-form
 * Total row. Built with jszip (already a dependency) — no SheetJS.
 * ------------------------------------------------------------------ */
import JSZip from 'jszip';
import { HOD_GRADES, type HodSubjectBlock } from '@/lib/hodAnalysis';

const GCOLS = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']; // 9 grade columns
const esc = (s: string) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const txt = (ref: string, s: string, style = 0) =>
  `<c r="${ref}"${style ? ` s="${style}"` : ''} t="inlineStr"><is><t xml:space="preserve">${esc(s)}</t></is></c>`;
const numC = (ref: string, n: number, style = 0) => `<c r="${ref}"${style ? ` s="${style}"` : ''}><v>${n}</v></c>`;
const fx = (ref: string, f: string, style = 0) => `<c r="${ref}"${style ? ` s="${style}"` : ''}><f>${esc(f)}</f></c>`;

interface SheetOut { name: string; xml: string; }

function buildSheet(block: HodSubjectBlock, titleLine: string): SheetOut {
  const rows: string[] = [];
  const merges: string[] = [];
  let r = 1;
  const row = (cells: string) => { rows.push(`<row r="${r}">${cells}</row>`); r += 1; };

  // Title + subtitle (merged across B:O).
  row(txt(`B${r}`, titleLine, 1)); merges.push(`B${r - 1}:O${r - 1}`);
  row(txt(`B${r}`, 'Teacher to Teacher Analysis')); merges.push(`B${r - 1}:O${r - 1}`);

  for (const form of block.forms) {
    // Header row: B=Teacher, C=form label, D..L grades, M Total, N/O percentages (all bold).
    const hdr =
      txt(`B${r}`, 'Teacher', 1) + txt(`C${r}`, form.formLabel, 1) +
      HOD_GRADES.map((g, i) => txt(`${GCOLS[i]}${r}`, g, 1)).join('') +
      txt(`M${r}`, 'Total', 1) + txt(`N${r}`, 'Credit Pass %', 1) + txt(`O${r}`, 'Pass %', 1);
    row(hdr);

    const first = r; // first data row
    for (const dr of form.rows) {
      const cells =
        txt(`B${r}`, dr.teacherName, 1) + txt(`C${r}`, dr.classLabel, 1) +
        HOD_GRADES.map((g, i) => numC(`${GCOLS[i]}${r}`, dr.gradeCounts[g] || 0, 1)).join('') +
        fx(`M${r}`, `SUM(D${r}:L${r})`) +
        fx(`N${r}`, `IFERROR(SUM(D${r}:G${r})/M${r},0)`, 2) +
        fx(`O${r}`, `IFERROR(SUM(D${r}:I${r})/M${r},0)`, 2);
      row(cells);
    }
    const last = r - 1; // last data row

    // Total row.
    const totCells =
      txt(`C${r}`, 'Total', 1) +
      GCOLS.map((c) => fx(`${c}${r}`, `SUM(${c}${first}:${c}${last})`, 1)).join('') +
      fx(`M${r}`, `SUM(D${r}:L${r})`, 1) +
      fx(`N${r}`, `IFERROR(SUM(D${r}:G${r})/M${r},0)`, 2) +
      fx(`O${r}`, `IFERROR(SUM(D${r}:I${r})/M${r},0)`, 2);
    row(totCells);
    r += 1; // blank spacer row between blocks
  }

  const cols =
    '<cols>' +
    '<col min="2" max="2" width="16" customWidth="1"/>' +
    '<col min="3" max="3" width="12" customWidth="1"/>' +
    '<col min="4" max="12" width="5" customWidth="1"/>' +
    '<col min="13" max="13" width="8" customWidth="1"/>' +
    '<col min="14" max="15" width="14" customWidth="1"/>' +
    '</cols>';
  const mergeXml = merges.length ? `<mergeCells count="${merges.length}">${merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>` : '';
  const xml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    cols + `<sheetData>${rows.join('')}</sheetData>${mergeXml}</worksheet>`;

  // Excel sheet-name rules: <=31 chars, none of []:*?/\
  let name = (block.subjectName || 'Subject').replace(/[[\]:*?/\\]/g, ' ').slice(0, 31).trim() || 'Subject';
  return { name, xml };
}

export async function buildHodAnalysisXlsxBlob(blocks: HodSubjectBlock[], titleLine: string): Promise<Blob> {
  const zip = new JSZip();
  // Deduplicate sheet names (Excel forbids duplicates).
  const used = new Set<string>();
  const sheets = blocks.map((b) => {
    const s = buildSheet(b, titleLine);
    let nm = s.name; let i = 2;
    while (used.has(nm.toLowerCase())) { nm = `${s.name.slice(0, 28)} ${i}`; i += 1; }
    used.add(nm.toLowerCase());
    return { ...s, name: nm };
  });
  if (sheets.length === 0) sheets.push({ name: 'Analysis', xml: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData/></worksheet>` });

  zip.file('[Content_Types].xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('') +
    `</Types>`);

  zip.file('_rels/.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`);

  zip.file('xl/workbook.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets>` +
    sheets.map((s, i) => `<sheet name="${esc(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
    `</sheets><calcPr fullCalcOnLoad="1"/></workbook>`);

  zip.file('xl/_rels/workbook.xml.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('') +
    `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    `</Relationships>`);

  zip.file('xl/styles.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>` +
    `<fills count="1"><fill><patternFill patternType="none"/></fill></fills>` +
    `<borders count="1"><border/></borders>` +
    `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
    `<cellXfs count="3">` +
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
    `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
    `<xf numFmtId="9" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>` +
    `</cellXfs>` +
    `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
    `</styleSheet>`);

  sheets.forEach((s, i) => zip.file(`xl/worksheets/sheet${i + 1}.xml`, s.xml));

  return zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
